/**
 * bronvraag.js — eigen bronnen bij Mens & maatschappij (geschiedenis, aardrijkskunde
 * e.a.; besluit Yasar 15-10-2026, optie `bronnen` in config/vakken.js).
 *
 * Een bron is een tekst, afbeelding (ook een kaart of spotprent), tabel of een
 * combinatie, met een titel en een bronvermelding. Eén of meer vragen hangen eraan.
 *
 * Geen nieuwe kolommen: een bron gebruikt de velden van de CE-vragenbank
 * (migratie 077), die al door opslaan, kopiëren, exam-proxy, afname, inzage en
 * nakijken gaan:
 *   bron_id         'eigen:<id>'  groepssleutel (bronGroepen.js: bron_id gaat voor)
 *   bron_inhoud     blokken (FORMAT.md §5): tekst, figuur, tabel, en een tekstblok
 *                   met stijl 'bronvermelding'. Elk blok heeft `rol` zodat de
 *                   editor het weer kan uitlezen.
 *   leestekst       platte versie (voorlezen, AI-nakijken, oude weergaven)
 *   leestekst_titel 'Bron 2: Spotprent uit 1932', nummer bij opslaan gezet
 *
 * In de wizard is `leestekstTitel` alleen de titel; het nummer volgt uit de
 * volgorde van de bronnen in de toets (bronNummers) en komt er bij opslaan voor.
 */

import { bronSleutel, groepeerVragen, voegVraagBijBron } from './bronGroepen';

export const EIGEN_BRON_PREFIX = 'eigen:';

export function isEigenBron(v) {
  const id = v?.bronId ?? v?.bron_id;
  return typeof id === 'string' && id.startsWith(EIGEN_BRON_PREFIX);
}

/** Nieuwe, unieke groepssleutel voor een bron. */
export function nieuweBronId() {
  const rnd = Math.random().toString(36).slice(2, 8);
  return `${EIGEN_BRON_PREFIX}${Date.now().toString(36)}${rnd}`;
}

/** Velden voor een nieuwe vraag die een nieuwe, nog lege bron start. */
export function nieuweBronVelden() {
  return { bronId: nieuweBronId(), bronInhoud: [], leestekst: '', leestekstTitel: '' };
}

export const LEGE_BRON = Object.freeze({
  bronvermelding: '',
  tekst: '',
  afbeeldingUrl: null,
  afbeeldingAlt: '',
  tabelTekst: '',
  tabelKop: true,
});

// ── Tabel ─────────────────────────────────────────────────────────────────────

/**
 * Tekst naar rijen: één regel per rij, kolommen gescheiden door een tab (plakken
 * uit Excel of Word) of anders door '|'. Lege regels vallen weg; korte rijen
 * worden aangevuld tot de breedste rij.
 */
export function tabelUitTekst(tekst, metKop = true) {
  const regels = String(tekst || '').split(/\r?\n/).filter((r) => r.trim() !== '');
  if (regels.length === 0) return null;
  const scheiding = regels.some((r) => r.includes('\t')) ? '\t' : '|';
  let rijen = regels.map((r) => {
    let cellen = r.split(scheiding).map((c) => c.trim());
    // "| a | b |" → randen weg
    if (scheiding === '|' && cellen.length > 1) {
      if (cellen[0] === '') cellen = cellen.slice(1);
      if (cellen[cellen.length - 1] === '') cellen = cellen.slice(0, -1);
    }
    return cellen;
  });
  const breedte = Math.max(...rijen.map((r) => r.length));
  rijen = rijen.map((r) => [...r, ...Array(breedte - r.length).fill('')]);
  if (metKop && rijen.length > 1) return { kop: [rijen[0]], rijen: rijen.slice(1) };
  return { kop: [], rijen };
}

function celTekst(c) {
  return typeof c === 'string' ? c : (c?.md ?? '');
}

/** Tabelblok terug naar tab-gescheiden tekst (voor het tekstvak in de editor). */
export function tabelNaarTekst(blok) {
  if (!blok) return '';
  return [...(blok.kop || []), ...(blok.rijen || [])]
    .map((r) => r.map(celTekst).join('\t'))
    .join('\n');
}

// ── Velden ↔ blokken ──────────────────────────────────────────────────────────

/** Editorvelden naar blokken in vaste volgorde: tekst, afbeelding, tabel, bronvermelding. */
export function bronVeldenNaarBlokken(velden) {
  const v = { ...LEGE_BRON, ...(velden || {}) };
  const blokken = [];
  String(v.tekst || '')
    .split(/\n\s*\n/)
    .map((a) => a.trim())
    .filter(Boolean)
    .forEach((md) => blokken.push({ type: 'tekst', rol: 'tekst', md }));
  if (v.afbeeldingUrl) {
    blokken.push({ type: 'figuur', rol: 'afbeelding', url: v.afbeeldingUrl, alt: (v.afbeeldingAlt || '').trim() || null });
  }
  const tabel = tabelUitTekst(v.tabelTekst, v.tabelKop !== false);
  if (tabel) blokken.push({ type: 'tabel', rol: 'tabel', ...tabel });
  const bronvermelding = String(v.bronvermelding || '').trim();
  if (bronvermelding) blokken.push({ type: 'tekst', rol: 'bronvermelding', stijl: 'bronvermelding', md: bronvermelding });
  return blokken;
}

/** Blokken terug naar editorvelden (omgekeerde van bronVeldenNaarBlokken). */
export function blokkenNaarBronVelden(blokken) {
  const velden = { ...LEGE_BRON };
  const alineas = [];
  (Array.isArray(blokken) ? blokken : []).forEach((b) => {
    if (!b) return;
    if (b.rol === 'bronvermelding' || b.stijl === 'bronvermelding') { velden.bronvermelding = b.md || ''; return; }
    if (b.type === 'figuur' && !velden.afbeeldingUrl) { velden.afbeeldingUrl = b.url || null; velden.afbeeldingAlt = b.alt || ''; return; }
    if (b.type === 'tabel' && !velden.tabelTekst) {
      velden.tabelTekst = tabelNaarTekst(b);
      velden.tabelKop = (b.kop || []).length > 0;
      return;
    }
    if (b.type === 'tekst' || b.type === 'kop') alineas.push(b.md || '');
  });
  velden.tekst = alineas.join('\n\n');
  return velden;
}

// *cursief* zonder regex-lookbehind (Safari/iPadOS < 16.4), zie geenLookbehind.test.js.
export const CURSIEF = /\*(?=\S)(.*?\S)\*/g;

function plat(md) {
  return String(md || '').replace(/\*\*(.+?)\*\*/g, '$1').replace(CURSIEF, '$1');
}

/**
 * Platte tekst van een bron: voor voorlezen, AI-nakijken en weergaven die geen
 * blokken kennen. Nooit leeg als er iets in de bron staat.
 */
export function bronPlatteTekst(velden) {
  const v = { ...LEGE_BRON, ...(velden || {}) };
  const delen = [];
  String(v.tekst || '').split(/\n\s*\n/).map((a) => plat(a.trim())).filter(Boolean).forEach((a) => delen.push(a));
  if (v.afbeeldingUrl) delen.push(v.afbeeldingAlt?.trim() ? `Afbeelding: ${v.afbeeldingAlt.trim()}` : 'Afbeelding');
  const tabel = tabelUitTekst(v.tabelTekst, v.tabelKop !== false);
  if (tabel) [...tabel.kop, ...tabel.rijen].forEach((r) => delen.push(r.join(' | ')));
  if (String(v.bronvermelding || '').trim()) delen.push(`Bron: ${v.bronvermelding.trim()}`);
  return delen.join('\n\n');
}

/** Velden voor pasBronToe: alles wat bij de groep mee moet na een bewerking. */
export function bronVeldenVoorGroep(velden) {
  return { bronInhoud: bronVeldenNaarBlokken(velden), leestekst: bronPlatteTekst(velden) };
}

// ── Nummering ─────────────────────────────────────────────────────────────────

/** Map sleutel → nummer (1, 2, …) van de eigen bronnen, op volgorde in de toets. */
export function bronNummers(vragen) {
  const nummers = new Map();
  groepeerVragen(vragen).forEach((g) => {
    if (g.sleutel && isEigenBron(g.vragen[0]) && !nummers.has(g.sleutel)) nummers.set(g.sleutel, nummers.size + 1);
  });
  return nummers;
}

const NUMMER_PREFIX = /^\s*Bron\s+\d+\s*(?::\s*)?/i;

/** "Bron 2: Spotprent" → "Spotprent" (bij het laden in de wizard). */
export function titelZonderNummer(titel) {
  return String(titel || '').replace(NUMMER_PREFIX, '');
}

export function titelMetNummer(nummer, titel) {
  const t = titelZonderNummer(titel).trim();
  return t ? `Bron ${nummer}: ${t}` : `Bron ${nummer}`;
}

/**
 * Vóór opslaan: elke vraag bij een eigen bron krijgt de titel met nummer
 * ("Bron 2: …"). Vragen zonder eigen bron blijven ongewijzigd.
 */
export function nummerBronnenVoorOpslag(vragen) {
  const nummers = bronNummers(vragen);
  return (vragen || []).map((v) => {
    if (!isEigenBron(v)) return v;
    const nr = nummers.get(bronSleutel(v));
    return nr ? { ...v, leestekstTitel: titelMetNummer(nr, v.leestekstTitel) } : v;
  });
}

// ── AI-vragen bij een bron (16-10-2026) ───────────────────────────────────────
// De AI krijgt alleen wat de docent in de bron zette: titel, tekst, tabel,
// bronvermelding en de beschrijving van een afbeelding. De afbeelding zelf (en de
// url) gaat niet mee. Zie backend/src/ai/generatorBron.js.

const GETAL_CEL = /^[-−+]?\d[\d.,]*\s*[a-zA-Zµ°%²³/]*$/;
const isGetalCel = (c) => GETAL_CEL.test(String(c ?? '').trim());

/** Staat er genoeg in de bron voor AI-vragen: tekst, een tabel of een beschrijving van de afbeelding? */
export function bronKanAiVragen(velden) {
  const v = { ...LEGE_BRON, ...(velden || {}) };
  return String(v.tekst || '').trim().length >= 10
    || !!tabelUitTekst(v.tabelTekst, v.tabelKop !== false)
    || (!!v.afbeeldingUrl && String(v.afbeeldingAlt || '').trim().length >= 10);
}

/**
 * Heeft de tabel een rij of kolom met minstens twee getallen? Dan kan de AI er een
 * diagramvraag uit maken. De backend controleert het ook (en weigert anders met 400).
 */
export function bronHeeftCijfers(velden) {
  const v = { ...LEGE_BRON, ...(velden || {}) };
  const tabel = tabelUitTekst(v.tabelTekst, v.tabelKop !== false);
  return tabelHeeftCijfers(tabel);
}

/** Zelfde controle voor een losse tabel ({ kop, rijen } uit tabelUitTekst). */
export function tabelHeeftCijfers(tabel) {
  if (!tabel) return false;
  const raster = [...(tabel.kop || []), ...(tabel.rijen || [])];
  const rijMet = raster.some((r) => r.slice(1).filter(isGetalCel).length >= 2);
  const breedte = Math.max(0, ...raster.map((r) => r.length));
  let kolomMet = false;
  for (let c = 1; c < breedte && !kolomMet; c += 1) kolomMet = raster.filter((r) => isGetalCel(r[c])).length >= 2;
  return rijMet || kolomMet;
}

/** Tabel voor de AI: { kop: string[] | null, rijen: string[][] } of null. */
export function tabelVoorAi(tabel) {
  if (!tabel) return null;
  return { kop: (tabel.kop && tabel.kop[0]) || null, rijen: tabel.rijen || [] };
}

/** De bron zoals de AI hem krijgt (POST /api/generate-questions, veld `bron`). */
export function bronVoorAi(velden, { titel = '', nummer = 1 } = {}) {
  const v = { ...LEGE_BRON, ...(velden || {}) };
  return {
    nummer,
    titel: titelZonderNummer(titel).trim(),
    tekst: String(v.tekst || '').split(/\n\s*\n/).map((a) => plat(a.trim())).filter(Boolean).join('\n\n'),
    tabel: tabelVoorAi(tabelUitTekst(v.tabelTekst, v.tabelKop !== false)),
    bronvermelding: String(v.bronvermelding || '').trim(),
    afbeelding: !!v.afbeeldingUrl,
    afbeeldingAlt: v.afbeeldingUrl ? String(v.afbeeldingAlt || '').trim() : '',
  };
}

// Een vraag die net is toegevoegd en nog niets heeft (geen tekst, opties of media).
function isLegeVraag(v) {
  if (!v || v.type === 'info') return false;
  if (String(v.tekst || '').trim()) return false;
  if (v.afbeelding || v.videoUrl || v.audioUrl || v.grafiekConfig) return false;
  if (Array.isArray(v.opties) && v.opties.some((o) => String(typeof o === 'string' ? o : o?.text ?? '').trim())) return false;
  return true;
}

/**
 * AI-vragen bij een eigen bron: direct achter de groep, met dezelfde bronvelden
 * (bron_id, blokken, platte tekst, titel; het model zet die nooit). Een lege vraag
 * in de groep, zoals de eerste vraag na "Voeg bron toe", maakt plaats.
 * @returns {{ vragen: object[], eerste: number }} eerste = index van de eerste nieuwe vraag
 */
export function voegAiVragenBijBron(vragen, sleutel, nieuw) {
  let lijst = [...(vragen || [])];
  const nieuwe = nieuw || [];
  const groep = groepeerVragen(lijst).find((g) => g.sleutel === sleutel);
  if (!groep || nieuwe.length === 0) return { vragen: [...lijst, ...nieuwe], eerste: lijst.length };
  // Na elkaar achter de groep: de groep groeit mee, dus de volgorde blijft.
  nieuwe.forEach((v) => { lijst = voegVraagBijBron(lijst, sleutel, v); });
  const nieuweIds = new Set(nieuwe.map((v) => v.id));
  lijst = lijst.filter((v) => nieuweIds.has(v.id) || bronSleutel(v) !== sleutel || !isLegeVraag(v));
  return { vragen: lijst, eerste: Math.max(0, lijst.findIndex((v) => v.id === nieuwe[0].id)) };
}

/**
 * Bron uit het boek (17-10-2026, "Vragen uit boek (AI)" → Bronvraag): de backend
 * geeft { titel, tekst, bronvermelding } met een passage letterlijk uit de boektekst,
 * en sinds 18-10-2026 soms een foto uit het boek (afbeeldingUrl, afbeeldingAlt = de
 * uitleg uit het boek). Velden voor elke vraag van die nieuwe bron; het nummer komt er
 * bij opslaan voor.
 */
export function bronVeldenUitBoek(bron) {
  const velden = {
    ...LEGE_BRON,
    tekst: String(bron?.tekst || ''),
    bronvermelding: String(bron?.bronvermelding || ''),
    afbeeldingUrl: bron?.afbeeldingUrl || null,
    afbeeldingAlt: bron?.afbeeldingUrl ? String(bron?.afbeeldingAlt || '') : '',
  };
  return {
    bronId: nieuweBronId(),
    bronInhoud: bronVeldenNaarBlokken(velden),
    leestekst: bronPlatteTekst(velden),
    leestekstTitel: String(bron?.titel || '').trim(),
  };
}
